'use client';

import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { useEffect, useState } from 'react';

import type { Content } from '@prismicio/client';
import type { Simplify } from '../../../../prismicio-types';

import { BoxContainer, Container } from '@/components';
import { useVisible } from '@/hooks';
import { ChartData, ChartStyles } from '../types/chart';
import { ChartContent } from './ChartContent/ChartContent';
import { Statistics } from './Statistics/Statistics';
import { ProspectData } from '@/types/salesforce';
import { usePathname } from 'next/navigation';
import { getPageName } from '@/utils';
import { routes } from '@/constants';
import { CreditRating } from '@/types/form';
import styles from './chartInner.module.scss';

const engineLandingLabel = (data?: ProspectData) => {
  if (!data) return null;
  if (data.ApproximateCreditRating__c === CreditRating.excellent) {
    return 503;
  }
  if (data.ApproximateCreditRating__c === CreditRating.good) {
    return 778;
  }
  if (data.ApproximateCreditRating__c === CreditRating.fair) {
    return 376;
  }
  if (data.ApproximateCreditRating__c === CreditRating.poor) {
    return 229;
  }
  return null;
};

interface ChartInnerProps {
  data: Simplify<Content.ChartSliceDefaultPrimary>;
  variation: 'default' | 'lightTheme';
  prospect?: ProspectData;
}

const components: JSXMapSerializer = {
  heading2: ({ children }) => <h2 className={styles.title}>{children}</h2>,
};

function hasStatistics(
  data: ChartInnerProps['data']
): data is Simplify<Content.ChartSliceDefaultPrimary> {
  return 'statistics' in data;
}

export const ChartInner = ({ data, variation, prospect }: ChartInnerProps) => {
  const [maxValue, setMaxValue] = useState<number>(0);
  const [isVisible, id] = useVisible();
  const pathname = usePathname();

  const pageName = getPageName(pathname);
  const isEngineLanding = pageName === routes.engineLanding;

  const firstChartStyles: ChartStyles = {
    heading: `${styles.first_heading} ${styles[variation]}`,
    barBackground: styles.first_bar_background,
    background: styles.first_background,
    color: styles.first_color ?? '',
  };
  const secondChartStyles: ChartStyles = {
    heading: styles.second_heading,
    barBackground: styles.second_bar_background,
    background: styles.second_background,
    color: `${styles.second_color} ${styles[variation]}`,
    axisLabel: styles.secondAxisLabel,
  };

  const chartsData: ChartData[] = [
    {
      heading: data.first_chart_heading,
      data: data.first_chart_data,
      styles: firstChartStyles,
    },
    {
      heading: data.second_chart_heading,
      data: data.second_chart_data,
      styles: secondChartStyles,
    },
  ];

  const calculateMaxValue = (datas: ChartData[]) => {
    const maxValues = datas.map((item) =>
      item.data.reduce(
        (max, chartData) =>
          Number(chartData.value) > max ? Number(chartData.value) : max,
        0
      )
    );
    return Math.ceil(Math.max(...maxValues) / 320) * 320;
  };

  useEffect(() => {
    setMaxValue(calculateMaxValue(chartsData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BoxContainer styles={`${styles.box} ${styles[variation]}`}>
      <Container id={id}>
        <div
          className={`${styles.title_wrapper} ${isVisible ? styles.isVisible : ''}`}
        >
          {isEngineLanding ? (
            <h2 className={`${styles.title} ${styles.engineTitle}`}>
              Over{' '}
              <span className={styles.categoryType}>
                <span>${engineLandingLabel(prospect)} million</span>
              </span>{' '}
              has been funded for people in your credit range
            </h2>
          ) : (
            <PrismicRichText field={data.title} components={components} />
          )}
        </div>
        <div className={styles.charts_container}>
          {chartsData.map((item) => (
            <ChartContent
              {...item}
              maxValue={maxValue}
              key={item.heading}
              isVisible={isVisible}
            />
          ))}
        </div>
        {hasStatistics(data) && (
          <Statistics
            heading={data.statictics_heading}
            description={data.statistics_description}
            statistics={data.statistics}
          />
        )}
      </Container>
    </BoxContainer>
  );
};
